/**
 * The person's Tab layout on the open Account (ADR 0027): painted at once
 * from the browser's copy, confirmed by the CRM, and joined by lists newly
 * pinned on OnlyFans (read in the background, never written back).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLibraryDeps } from '../library/LibraryDeps'
import { useChatsDeps } from './ChatsDeps'
import { BUILTIN_TABS, defaultLayout, pruneLists, savedFor, syncLayout, type SavedLayout, type TabRef } from './tabLayout'
import { pageAll, type ListTab } from './useChatTabs'

interface Remembered {
  saved: SavedLayout | null
  pinned: ListTab[] | null
}

const storageKey = (scope: string) => `crm_chats_tab_layout_${scope}`

function readRemembered(scope: string): Remembered | null {
  try {
    const raw = JSON.parse(localStorage.getItem(storageKey(scope)) || 'null') as Remembered | null
    return raw && typeof raw === 'object' && 'saved' in raw ? raw : null
  } catch {
    return null
  }
}

function writeRemembered(scope: string, value: Remembered) {
  try {
    localStorage.setItem(storageKey(scope), JSON.stringify(value))
  } catch {
    /* storage unavailable */
  }
}

type LayoutAnswer = { available?: boolean; tabs?: TabRef[] | null; seen?: string[] }

const shown = (t: TabRef) => t.kind === 'list' || BUILTIN_TABS.includes(t.id)

export function useTabLayout() {
  const { gateway } = useLibraryDeps()
  const { local, storageScope } = useChatsDeps()
  const remembered = useMemo(() => readRemembered(storageScope), [storageScope])
  const [saved, setSaved] = useState<SavedLayout | null>(remembered?.saved ?? null)
  const [pinned, setPinned] = useState<ListTab[] | null>(remembered?.pinned ?? null)
  /** The CRM answered (until then the browser's copy is shown, never saved back). */
  const [confirmed, setConfirmed] = useState(false)
  const [available, setAvailable] = useState(true)
  const [failed, setFailed] = useState(false)
  const savedRef = useRef(saved)
  savedRef.current = saved

  useEffect(() => {
    let live = true
    local<LayoutAnswer>('/v1/chats/tab-layout', {}, 'GET').then(
      (d) => {
        if (!live) return
        setAvailable(d?.available !== false)
        setSaved(d?.tabs ? { tabs: d.tabs, seen: d.seen ?? [] } : null)
        setConfirmed(true)
      },
      () => live && setConfirmed(true),
    )
    pageAll(gateway, '/v1/lists/get-chat-tabs', 10).then(
      (rows) => live && setPinned(rows),
      () => undefined,
    )
    return () => {
      live = false
    }
  }, [local, gateway])

  useEffect(() => writeRemembered(storageScope, { saved, pinned }), [storageScope, saved, pinned])

  const store = useCallback(
    async (next: SavedLayout) => {
      const before = savedRef.current
      setSaved(next)
      setFailed(false)
      try {
        await local('/v1/chats/tab-layout', { tabs: next.tabs, seen: next.seen }, 'PUT')
      } catch {
        setSaved(before)
        setFailed(true)
      }
    },
    [local],
  )

  const view = useMemo(() => syncLayout(saved, pinned), [saved, pinned])

  // A saved layout picks up what OnlyFans newly pinned (or a new default tab) once.
  useEffect(() => {
    if (confirmed && available && saved && view.changed && view.seen) void store({ tabs: view.tabs, seen: view.seen })
  }, [confirmed, available, saved, view, store])

  const edit = useCallback((tabs: TabRef[]) => store(savedFor(savedRef.current, tabs, pinned)), [store, pinned])
  const reset = useCallback(() => store(savedFor(null, defaultLayout(pinned), pinned)), [store, pinned])
  /** OnlyFans' complete list of lists is known: list tabs whose list is gone leave the layout. */
  const prune = useCallback(
    (lists: ListTab[]) => {
      const next = pruneLists(view.tabs, lists)
      if (next.length !== view.tabs.length && savedRef.current) void edit(next)
    },
    [view.tabs, edit],
  )

  const tabs = useMemo(() => view.tabs.filter(shown), [view.tabs])
  return {
    tabs,
    /** The CRM answered: a tab missing from the bar now is really gone. */
    ready: confirmed,
    available: available && confirmed,
    failed,
    edit,
    reset,
    prune,
  }
}
